import maintenanceAccidentsQueries from '@/api/modules/maintenanceAccidentsQueries'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { handleApiError } from '@/utils/apiHelpers'

class MaintenanceAccidentsService {
    async fetchAccidents(args = {}) {
        try {
            return await maintenanceAccidentsQueries.fetchAccidents(args)
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentsError'))
            throw error
        }
    }

    async fetchAccident(accidentId) {
        try {
            return await maintenanceAccidentsQueries.fetchAccident(accidentId)
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentError', { accidentId }))
            throw error
        }
    }

    async fetchUnsolvedMachineAccidents(machineId) {
        try {
            return await maintenanceAccidentsQueries.fetchUnsolvedMachineAccidents(machineId)
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentsError'))
            throw error
        }
    }

    async fetchAccidentTypes() {
        try {
            return await maintenanceAccidentsQueries.fetchAccidentTypes()
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentTypesError'))
            throw error
        }
    }

    async fetchAccidentStatuses() {
        try {
            return await maintenanceAccidentsQueries.fetchAccidentStatuses()
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentStatusesError'))
            throw error
        }
    }

    async fetchAccidentStatusHistory(accidentId) {
        try {
            return await maintenanceAccidentsQueries.fetchAccidentStatusHistory(accidentId)
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentStatusHistoryError'))
            throw error
        }
    }

    async fetchMachinesBasicData() {
        try {
            return await maintenanceAccidentsQueries.fetchMachinesBasicData()
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchMachinesError'))
            throw error
        }
    }

    async fetchSoftwareTypes() {
        try {
            return await maintenanceAccidentsQueries.fetchSoftwareTypes()
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchSoftwareTypesError'))
            throw error
        }
    }

    async fetchAccidentDepartments() {
        try {
            return await maintenanceAccidentsQueries.fetchAccidentDepartments()
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentDepartmentsError'))
            throw error
        }
    }

    async fetchAccidentUserDepartments() {
        try {
            return await maintenanceAccidentsQueries.fetchAccidentUserDepartments()
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentUserDepartmentsError'))
            throw error
        }
    }

    async createAccident(payload) {
        try {
            const response = await maintenanceAccidentsQueries.createAccident(payload)
            notificationService.success(t('maintenance.service.createAccidentSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('maintenance.service.createAccidentError'))
            throw error
        }
    }

    async updateAccident(accidentData) {
        try {
            const response = await maintenanceAccidentsQueries.updateAccident(accidentData)
            notificationService.success(t('maintenance.service.updateAccidentSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('maintenance.service.updateAccidentError'))
            throw error
        }
    }

    async deleteAccident(accidentId) {
        try {
            return await maintenanceAccidentsQueries.deleteAccident(accidentId)
        } catch (error) {
            handleApiError(error, t('maintenance.service.deleteAccidentError'))
            throw error
        }
    }

    async createAccidentComment(payload) {
        try {
            const response = await maintenanceAccidentsQueries.createAccidentComment(payload)
            notificationService.success(t('maintenance.service.createAccidentCommentSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('maintenance.service.createAccidentCommentError'))
            throw error
        }
    }

    async generateAccidentsReport({ query = '', userEmail } = {}) {
        try {
            const response = await maintenanceAccidentsQueries.generateAccidentsReport({ query })
            notificationService.success(t('maintenance.service.generateAccidentsReportSuccess', { userEmail }))
            return response
        } catch (error) {
            handleApiError(error, t('maintenance.service.generateAccidentsReportError'))
            throw error
        }
    }

    async fetchAccidentDashboard({ query = '' } = {}) {
        try {
            return await maintenanceAccidentsQueries.fetchAccidentDashboard({ query })
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentDashboardError'))
            throw error
        }
    }

    async fetchAccidentDashboardMachines({ query = '' } = {}) {
        try {
            return await maintenanceAccidentsQueries.fetchAccidentDashboardMachines({ query })
        } catch (error) {
            handleApiError(error, t('maintenance.service.fetchAccidentDashboardMachinesError'))
            throw error
        }
    }

    async generateAccidentDashboardReport({ query = '', userEmail } = {}) {
        try {
            const response = await maintenanceAccidentsQueries.generateAccidentDashboardReport({ query })
            notificationService.success(t('maintenance.service.generateAccidentDashboardReportSuccess', { userEmail }))
            return response
        } catch (error) {
            handleApiError(error, t('maintenance.service.generateAccidentDashboardReportError'))
            throw error
        }
    }
}

export const useMaintenanceAccidentsService = (options) => new MaintenanceAccidentsService(options)
